import { useEffect } from "react";
import useState from "react-usestateref";
import * as XLSX from "xlsx";
import { Button, Spin, Typography, message } from "antd";
import JSZip from "jszip";
import { getToken } from "@/utils/localstorage";
import axios from "axios";
import { logger } from "@/helpers/tslog";
import { insertChartImage, PlaceholderInfo, validatePlaceholderExists, resumeChartProgress, extractAndInsertChart } from "@/helpers/chart-extraction-handlers";
import { fetchChartDocuments } from "@/utils/fetchChartDocuments";
import { updateButtonStatus } from "@/utils/contentReady/helper";
import { useEffectAsync } from "@alphascriptsolutions/client-core";
import { useSharePricePerformanceStore } from "@/state/sharePricePerformanceStore";
import { handleFetchTemplate } from "@/utils/sharePricePerformance/fetchFormatTemplates";
import { openContentFraming, updatePlaceholderPopupOpen } from "@/utils/popupDialogManager";
import { LoadingOutlined } from "@ant-design/icons";
import { fetchDocumentById, updateDocumentExcelInfo } from "@/utils/contentmanager/documents";
import { useContentFramingStore } from "@/state/contentFramingStore";
import getAllShapesFromPlaceholderSlide from "@/helpers/slide-wireframing/getAllShapesFromPlaceholderSlide";
const { Title, Text } = Typography;

const ContentReady = () => {
  const [selectedChartId, setSelectedChartId] = useState<string | null>(null);
  const [_, setCharts, chartsRef] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const { setMetaData } = useSharePricePerformanceStore();


  async function refreshCharts() {
    try {
      setIsLoading(true);
      

      let uuid = Office.context.document.settings.get("ALPHASCRIPT");
      const charts = await fetchChartDocuments(uuid);
      setCharts(charts);
      // Sync in-progress tracking ids in localStorage so sockets can rejoin after refresh
      try {
        const ids = (charts || [])
          .filter((c: any) => c?.isProgress && typeof c?.documentId === 'string')
          .map((c: any) => c.documentId);
        localStorage.setItem('inProgressDocumentIds', JSON.stringify(Array.from(new Set(ids))));
        // Ensure socket is connected and rejoined after data refresh
        await resumeChartProgress();
      } catch {}
    } finally {
      setIsLoading(false);
    }
  }

  useEffectAsync(async () => {
    await refreshCharts();
  }, []);

  // Ensure socket is connected and rejoins rooms on first mount as well
  useEffect(() => {
    void resumeChartProgress();
  }, []);

  // Listen for postMessage events to refresh charts
  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      try {
        const data: any = event?.data;
        if (data && typeof data === "object") {
          if (data.type === "REFRESH_CHARTS") {
            void refreshCharts();
          } else if (data.type === "CHART_PROGRESS_UPDATE") {
            const { documentId, progress, step } = data;
            setCharts((prev) => (prev || []).map((c: any) =>
              c.documentId === documentId
                ? { ...c, isProgress: true, progress: typeof progress === 'number' ? progress : c.progress, currentStep: step || c.currentStep }
                : c
            ));
          }
        }
      } catch {}
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  Office.onReady(() => {
    Office.addin.onVisibilityModeChanged(async (args) => {
      if (args.visibilityMode === "Hidden" && (chartsRef.current.length === 0)) {
        await updateButtonStatus(false);
      }
    });
  });

  const downloadTemplateFile = async (documentId: string, filename: string, headers = {}) => {
    logger.info("Starting download process for document:", documentId);
    const MAX_RETRIES = 3;

    for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
      try {
        const response = await axios.get(`${process.env.API_URL}/document-part`, {
          params: {
            documentId,
            type: "excel",
          },
          headers,
        });

        if (!response.data.files || response.data.files.length === 0) {
          throw new Error("No excel files found");
        }

        logger.info("Received response from API");

        const excelFile = response.data.files[0];
        logger.info("Excel file found, decoding base64 content");
        const binaryString = window.atob(excelFile.content);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }

        logger.info("Reading Excel workbook");
        const workbook = XLSX.read(bytes, { type: "array" });
        const sheet = workbook.Sheets["Share Price Chart"];
        if (!sheet) {
          logger.error("Sheet 'Share Price Performance' not found");
          throw new Error("Sheet not found");
        }

        logger.info("Reading source and footnotes from Excel");
        const sourceText = sheet["D30"]?.v?.toString() ?? "";
        const cellAddresses = ["D33", "D34", "D35", "E33", "E34", "E35"];
        const valuesArray = cellAddresses.flatMap((cell) => {
          const value = sheet[cell]?.v?.toString()?.trim();
          return value ? [value] : [];
        });
        logger.info("Metadata extracted:", { sourceText, valuesArray });

        // Prefer reading AlphaScript settings from customXml in the template (set by render-agent)
        let alphaSettings: any = null;
        try {
          const zip = await JSZip.loadAsync(excelFile.content, { base64: true });
          const customXmlFolder = zip.folder("customXml");
          if (customXmlFolder) {
            let xmlText: string | null = null;
            const candidates: string[] = [];

            await customXmlFolder.forEach((relativePath, _file) => {
              if (/\.xml$/i.test(relativePath)) {
                candidates.push(relativePath);
              }
            });

            for (const relPath of candidates) {
              const file = customXmlFolder.file(relPath);
              if (!file) continue;
              const text = await file.async("text");
              if (/\balphaScriptSettings\b/.test(text)) {
                xmlText = text;
                break;
              }
            }

            if (xmlText) {
              const doc = new DOMParser().parseFromString(xmlText, "application/xml");
              const ns = "http://alphascript.io/settings";
              const getVal = (tag: string) => doc.getElementsByTagNameNS(ns, tag)?.[0]?.textContent || null;
              alphaSettings = {
                workbookGuid: getVal("workbookGuid"),
                linkId: getVal("linkId"),
                chartName: getVal("chartName"),
                render: getVal("render"),
                generated: getVal("generated"),
              };
              logger.info("AlphaScript settings (customXml):", alphaSettings);
              // Only log values; do not persist to store/settings
            } else {
              logger.warn("AlphaScript settings customXml not found in workbook.");
            }
          } else {
            logger.warn("customXml folder not found in workbook.");
          }
        } catch (e) {
          logger.warn("Could not parse AlphaScript customXml settings (zip parse error)", e);
        }

        // Set metadata including AlphaScript settings
        setMetaData({
          alphaScriptSettings: alphaSettings,
        });

        const blob = new Blob([bytes], { type: excelFile.mimeType });

        // Upload to OneDrive (disabled for now) Implement later
        // try {
        //  const oneDriveUrlExcelFileUrl: string = await uploadToAlphaScriptFolder(blob, `${filename}.xlsx`);
        //   message.success(`Excel File Uploaded to OneDrive`);
        
        //   const metaData = useSharePricePerformanceStore.getState().metaData;
        //   setMetaData({
        //     ...metaData,
        //     excelFileUrl: oneDriveUrlExcelFileUrl,
        //   });
        //   logger.info("Uploaded to OneDrive:", `${filename}.xlsx`);
        // } catch (e) {
        //   logger.warn("OneDrive upload failed", e);
        // }

        // Prompt the user with the native Save As dialog
        logger.info("Attempting to prompt Save As dialog");
        const handle = await (window as any).showSaveFilePicker({
          suggestedName: filename + ".xlsx",
        });

        logger.info("Save file handle obtained, writing file...");
        const writable = await handle.createWritable();
        await writable.write(blob);
        await writable.close();

        const documentData = await fetchDocumentById(documentId);
        await updateDocumentExcelInfo(documentId, "Not implemented yet", filename, documentData);

        logger.info("File saved successfully!");
        return true;
      } catch (err) {
        logger.error(`Attempt ${attempt} failed:`, err);
        if (attempt === MAX_RETRIES) {
          logger.error("All 3 attempts failed. Could not save file.");
        } else {
          logger.warn(`Retrying... (${attempt + 1}/${MAX_RETRIES})`);
        }
      }
    }
    return false;
  };
  const handleGenerate = async (
    documentId: string,
    blob: Blob,
    placeholder: PlaceholderInfo,
    documentPartId: string,
    id: any,
    placeholderGap: any
  ) => {
    await Office.onReady();
    // Pre-check: ensure the placeholder still exists before starting extraction
    const placeholderOk = await validatePlaceholderExists(placeholder, placeholderGap);
    if (!placeholderOk) {
      message.error("Selected placeholder no longer exists on the slide");
      localStorage.setItem("ErrorChartData", JSON.stringify({ documentId, documentPartId, id, placeholder }));
      updatePlaceholderPopupOpen();
      return;
    }
    const token = await getToken();
    if (!documentId) {
      logger.error("No document ID found");
      return;
    }
    const date = new Date();
    const formattedDate = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

    // Format time in user's local timezone (24-hour format: HHMM_TZ)
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const timeZone = date.toLocaleTimeString("en-US", { timeZoneName: "short" }).split(" ").pop() || "";
    const formattedTime = `${hours}${minutes}_${timeZone}`;

    
    
    // Temporary arguments
    const downloaded = await downloadTemplateFile(
      documentId,
      `AlphaScript_Share_Price_Performance_Line_Chart_${documentId}_${formattedDate}_${formattedTime}`,
      {
        Authorization: `Bearer ${token}`,
      }
    );
    const { reset } = useContentFramingStore.getState();
    reset();
    await getAllShapesFromPlaceholderSlide(placeholder.slideId, placeholder.shapeId);

    if (!downloaded) {
      message.error("Excel failed to download. Please try again.", 2);
      logger.warn("Template download was not completed. Aborting archive and insert operations.");
      return;
    }

    
    openContentFraming();

    const apiUrl = process.env.API_URL;
    await axios.post(
      `${apiUrl}/document-part-archived`,
      { isArchived: true },
      {
        params: { documentPartId: documentPartId },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    setCharts((prev) => prev.filter((ph) => ph.id !== id));
    setSelectedChartId(null);
    await insertChartImage(blob, placeholder, documentId);
  };

  const insertChartToPlaceholder = async (
    id: any,
    blob: any,
    documentPartId: any,
    documentId: any,
    placeholder: any,
    placeholderGap: any
  ) => {
    await handleGenerate(documentId, blob, placeholder, documentPartId, id, placeholderGap);
  };

  async function handleInsertChart(chartId: string) {
    const chart = chartsRef.current.find((c) => c.id === chartId);
    if (!chart) return;

    setMetaData({
      notesData: chart.metadata.notes,
      sourceData: chart.metadata.source,
      titlePlaceholder: chart.titlePlaceholder,
      subTitlePlaceholder: chart.subTitlePlaceholder,
      subtitleRight: chart.subtitleRight,
      chartTitleData: chart.metadata.chartTitle,
      chartSubTitleData: chart.metadata.chartSubTitle,
      fontsSection: chart.fontsSection,
    });
    localStorage.setItem("metadata", JSON.stringify(chart.metadata));
    await handleFetchTemplate(chart.formatId);
    await insertChartToPlaceholder(chart.id, chart.blob, chart.documentPartId, chart.documentId, chart.placeholder, chart.metadata.placeholderGap);
  }

  
  if (isLoading)
    return (
      <div
        style={{
          width: "100%",
          height: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexDirection: "column",
          gap: "10px",
        }}
      >
        <Spin size="large" tip="Loading" />
        <Typography.Text>Loading</Typography.Text>
      </div>
    );

  return (
    <div style={{ width: "100%", fontFamily: "Arial, sans-serif" }}>
      <div
        style={{
          maxWidth: 640,
          margin: "0 auto",
          padding: 16,
          display: "flex",
          flexDirection: "column",
          gap: 32,
        }}
      >
        {chartsRef.current.length > 0 && (
          <>
            <Text style={{ marginBottom: 16 }}>
              <span style={{ fontWeight: "bold", color: "#FF5C35" }}>Select the content image below</span> to add it to
              the placeholder and download the Excel file.
            </Text>
            <PlaceholderUI
              charts={chartsRef.current}
              setSelectedChartId={setSelectedChartId}
              selectedChartId={selectedChartId}
              onChartDeleted={refreshCharts}
            />
          </>
        )}
        {chartsRef.current.length === 0 && (
          <Title level={5} style={{ color: "black", textAlign: "center" }}>
            You have no outstanding content that is waiting to be deployed. You may close the taskpane.
          </Title>
        )}
      </div>
      {selectedChartId && (
        <div
          style={{
            position: "fixed",
            bottom: 0,
            left: 0,
            zIndex: 1000,
            maxWidth: "100%",
            backgroundColor: "#fff",
            padding: "24px 36px",
            boxSizing: "border-box",
            display: "flex",
            justifyContent: "center",
            gap: 12,
          }}
        >
          <Button
            type="primary"
            onClick={() => handleInsertChart(selectedChartId)}
            style={{ backgroundColor: "#1f7d3d" }}
          >
            Insert
          </Button>
          <Button onClick={() => setSelectedChartId(null)}>Cancel</Button>
        </div>
      )}
    </div>
  );
};

export default ContentReady;

function PlaceholderUI({ charts, setSelectedChartId, selectedChartId, onChartDeleted }) {

  const handleRetryChart = async (chart: any) => {
    message.info("Retrying chart...");
    const documentData = await fetchDocumentById(chart.documentId);
   
    localStorage.setItem("ModelDataToBackend", documentData?.documentContent);
    localStorage.setItem("modelGlobalState", documentData?.llmGlobalState);
    localStorage.setItem("alphaEdit", "true");
    localStorage.setItem("documentId", chart.documentId);
    extractAndInsertChart();
   
  };

  const handleDeleteChart = async (chart: any) => {
    try {
      const token = await getToken();
      const apiUrl = process.env.API_URL;
      
      message.loading({ content: "Deleting chart...", key: "delete" });
      
      const response = await axios.delete(`${apiUrl}/document`, {
        params: {
          documentId: chart.documentId,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.status === 200) {
        message.success({ content: "Chart deleted successfully", key: "delete" });
        // Refresh charts list
        if (onChartDeleted) {
          await onChartDeleted();
        }
        // Clear selection if deleted chart was selected
        if (selectedChartId === chart.id) {
          setSelectedChartId(null);
        }
      } else {
        throw new Error("Failed to delete chart");
      }
    } catch (error: any) {
      logger.error("Error deleting chart:", error);
      message.error({ 
        content: error?.response?.data?.error || "Failed to delete chart. Please try again.", 
        key: "delete" 
      });
    }
  };


  return (
    <>
      {charts.map((ch) => (
        <div key={ch.id} style={{ marginBottom: 32, width: "100%" }}>
          <h3 style={{ fontWeight: "600", marginBottom: 8 }} className={"underline text-[15px]"}>
            Chart {ch.id}
          </h3>
          <div
            onClick={() => {
              if (ch.isProgress || ch.hasChartError) return;
              setSelectedChartId(ch.id);
            }}
            style={{
              border: selectedChartId === ch.id ? "2px solid #1f7d3d" : "2px solid #FF5C35",
              height: 160,
              width: "100%",
              maxWidth: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: 8,
              cursor: ch.isProgress || ch.hasChartError ? "not-allowed" : "pointer",
              backgroundColor: "#fafafa",
              borderRadius: "4px",
              padding: 8,
            }}
          >
            {ch.isProgress ? (
              <div style={{ width: "100%", textAlign: "center", padding: "8px" }}>
                <div style={{ marginBottom: 8, display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
                  <LoadingOutlined style={{ color: "#FF5C35" }} spin />
                  <Text style={{ fontSize: 16 }}>{Math.round(ch.progress ?? 0)}% Complete</Text>
                </div>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Text style={{ fontSize: 16 }}>
                    <span style={{ fontWeight: 600 }}>Step:</span> {ch.currentStep || "Preparing..."}
                  </Text>
                </div>
              </div>
            ) : ch.hasChartError ? (
              <div style={{ textAlign: "center" }}>
                <div style={{ color: "#b91c1c", fontWeight: 600, marginBottom: 8 }}>
                  Chart generation failed. Select to retry inserting.
                </div>
                <div className=" w-full  flex justify-center gap-2">
                  <Button type="primary" danger onClick={() => handleRetryChart(ch)}>
                    Retry
                  </Button>
                  <Button type="primary" danger onClick={() => handleDeleteChart(ch)}>
                    Delete
                  </Button>
                </div>
              </div>
            ) : ch.content ? (
              <img src={ch.content} alt={`Chart ${ch.id}`} style={{ maxHeight: "100%", maxWidth: "100%" }} />
            ) : (
              <span style={{ color: "#6b7280", textAlign: "center" }}>[Image to be included in placeholder]</span>
            )}
          </div>
          <p style={{ fontSize: 14, fontWeight: "500" }}>
            {ch.contentType}
          </p>
        </div>
      ))}
    </>
  );
}
